/**
 * Electron offline-first sync service.
 * Orchestrates: Clerk org setup → Bible download → VPS pull → local push → periodic sync.
 * Only active when window.electronAPI is present.
 *
 * Web-browser sync: subscribes to /api/sync/events SSE and dispatches
 * 'proclaim:sync' CustomEvent so stores know to refetch.
 */

const LAST_SEQ_KEY = 'proclaim_last_seq';                  // VPS→local pull cursor (sequence number)
const LAST_PUSHED_SEQ_KEY = 'proclaim_last_pushed_seq';    // local→VPS push cursor (sequence number)
const BIBLE_LOCAL_COUNT_KEY = 'proclaim_bible_local_count';
const SYNC_INTERVAL_MS = 5 * 60 * 1000; // 5 min

let _syncTimer: ReturnType<typeof setInterval> | null = null;
let _scheduleTimer: ReturnType<typeof setTimeout> | null = null;
let _sseAbort: AbortController | null = null;
let _webSseAbort: AbortController | null = null;
let _webSyncTimer: ReturnType<typeof setInterval> | null = null;
let _vpsUrl = 'https://proclaim.iglesiaebenezer.mx';
let _token: string | null = null;                                              // logout guard for SSE loops
let _getToken: ((forceRefresh?: boolean) => Promise<string | null>) | null = null; // always-fresh token
// VPS auth scheme: 'bearer' = Clerk JWT (web + dev:electron); 'desktop' = backend
// desktop session token (prod Electron, sent as X-Desktop-Token).
let _authScheme: 'bearer' | 'desktop' = 'bearer';
// Module-level guard: prevents re-initialization when the bridge remounts (page nav,
// React StrictMode) without a full org change. Reset to null on logout.
let _initializedForOrg: string | null = null;

// Mutex: coalesces concurrent runSync() calls (SSE + scheduleSync + interval can overlap).
// Without this, two concurrent runs read the same cursor, double-pull, and the slower
// run's cursor write can clobber the fresher run's value.
let _running = false;
let _pending = false;

// VPS auth header. Prod Electron uses the backend desktop session token
// (X-Desktop-Token); web + dev:electron use the Clerk JWT (Authorization: Bearer).
function vpsAuthHeaders(token: string | null): Record<string, string> {
  if (!token) return {};
  return _authScheme === 'desktop' ? { 'X-Desktop-Token': token } : { Authorization: `Bearer ${token}` };
}

async function fetchWithAuth(url: string, token: string | null, options?: RequestInit): Promise<Response> {
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...vpsAuthHeaders(token),
      ...(options?.headers as Record<string, string> | undefined),
    },
  });
}

async function vpsRequest<T>(path: string, options?: RequestInit): Promise<T> {
  let token = _getToken ? await _getToken() : _token;
  let res = await fetchWithAuth(`${_vpsUrl}/api${path}`, token, options);

  // On 401 or 403: force-refresh Clerk token and retry once.
  // 401 = token expired/invalid. 403 from /sync/push = JWT missing org_id claim
  // (cached token issued before org was activated — force-refresh adds org claims).
  if ((res.status === 401 || res.status === 403) && _getToken) {
    console.warn(`[Sync] ${res.status} on VPS request, refreshing token and retrying...`);
    token = await _getToken(true);
    res = await fetchWithAuth(`${_vpsUrl}/api${path}`, token, options);
  }

  const json = await res.json();
  if (!json.success) throw new Error(json.error ?? 'VPS request failed');
  return json.data as T;
}

async function localRequest<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options?.headers as Record<string, string> | undefined),
    },
  });
  const json = await res.json();
  if (!json.success) throw new Error(json.error ?? 'Local request failed');
  return json.data as T;
}

async function downloadBibleIfNeeded(): Promise<void> {
  const cached = Number(localStorage.getItem(BIBLE_LOCAL_COUNT_KEY) ?? 0);
  if (cached > 1000) return;

  // Check local DB — seed DB bundled with installer may already have Bible
  try {
    const books = await localRequest<Array<unknown>>('/bible/books');
    if (books.length > 0) {
      localStorage.setItem(BIBLE_LOCAL_COUNT_KEY, String(books.length * 1000));
      return;
    }
  } catch { /* server not ready, continue to VPS download */ }

  console.log('[Sync] Downloading Bible from VPS...');
  const { books, verses } = await vpsRequest<{
    books: Array<Record<string, unknown>>;
    verses: Array<Record<string, unknown>>;
  }>('/bible/download');

  // Verses from /download have bookId (camelCase) — add version field
  const versesWithVersion = verses.map((v) => ({ ...v, version: v.version ?? 'RVR1960' }));

  const result = await window.electronAPI!.sync.importBible(books, versesWithVersion);
  if (!result.ok) throw new Error(`Bible import failed: ${result.error}`);

  localStorage.setItem(BIBLE_LOCAL_COUNT_KEY, String(result.verses ?? verses.length));
  console.log(`[Sync] Bible imported: ${result.books} books, ${result.verses} verses`);
}

// Handles both response shapes:
// - New: { ops: SyncOp[] } (after server fix)
// - Old VPS: SyncPullResult (per-table arrays, pre-fix)
const PULL_TABLE_KEYS: Array<[string, string]> = [
  ['presentations', 'presentations'],
  ['slides', 'slides'],
  ['songs', 'songs'],
  ['songSections', 'song_sections'],
  ['songArrangements', 'song_arrangements'],
  ['customFonts', 'custom_fonts'],
  ['customFontFiles', 'custom_font_files'],
  ['uploadedImages', 'uploaded_images'],
];

function normalizeOps(data: Record<string, unknown>): unknown[] {
  if (Array.isArray(data.ops)) return data.ops as unknown[];
  // Legacy per-table format — flatten to SyncOp
  const ops: unknown[] = [];
  for (const [key, table] of PULL_TABLE_KEYS) {
    const rows = data[key];
    if (!Array.isArray(rows)) continue;
    for (const row of rows as Record<string, unknown>[]) {
      ops.push({
        table,
        recordId: row.id,
        operation: row.deletedAt ? 'delete' : 'upsert',
        payload: row,
        clientTimestamp: new Date(row.updatedAt as string).getTime(),
      });
    }
  }
  return ops;
}

const PUSH_BATCH_SIZE = 200;
// Cap each push body well under the server's 25mb express.json limit. Canvas
// slides with long verses can be a few KB each; batching purely by count let a
// big presentation's batch cross 100kb (the old default) and 500 with the new
// limit is still worth guarding. Split by serialized byte size so a single
// request can never blow the body-parser limit regardless of op contents.
const PUSH_BATCH_BYTES = 4 * 1024 * 1024; // 4 MB

type PushResult = { applied: number; skipped: number; errors: string[] };

// Split ops into batches bounded by BOTH op count and serialized byte size.
// A single oversized op still ships alone (better a clear per-op error than a
// silent body-parser 500 that drops the whole batch).
function batchOps<T>(ops: T[]): T[][] {
  const batches: T[][] = [];
  let batch: T[] = [];
  let bytes = 0;
  for (const op of ops) {
    const opBytes = JSON.stringify(op).length;
    if (batch.length > 0 && (batch.length >= PUSH_BATCH_SIZE || bytes + opBytes > PUSH_BATCH_BYTES)) {
      batches.push(batch);
      batch = [];
      bytes = 0;
    }
    batch.push(op);
    bytes += opBytes;
  }
  if (batch.length > 0) batches.push(batch);
  return batches;
}

async function pullFromVps(): Promise<{ count: number; serverTime: string | null; serverSeq: number | null }> {
  const seqStr = localStorage.getItem(LAST_SEQ_KEY);
  const sinceSeq = seqStr !== null ? seqStr : '0';

  const data = await vpsRequest<Record<string, unknown>>(`/sync/pull?since_seq=${sinceSeq}`);
  const ops = normalizeOps(data);
  const serverTime = (data.serverTime as string | undefined) ?? null;
  const serverSeq = typeof data.serverSeq === 'number' ? data.serverSeq : null;

  if (!ops.length) {
    console.log(`[Sync] VPS pull since_seq=${sinceSeq}: 0 ops (serverSeq=${serverSeq})`);
    return { count: 0, serverTime, serverSeq };
  }

  const byTable = (ops as Array<{ table?: string }>).reduce<Record<string, number>>((acc, op) => {
    const t = op.table ?? 'unknown';
    acc[t] = (acc[t] ?? 0) + 1;
    return acc;
  }, {});
  console.log(`[Sync] VPS pull since_seq=${sinceSeq}: ${ops.length} ops`, byTable);

  // Push to local in batches bounded by count AND byte size (stays under the
  // server's body-parser limit + syncPushSchema max(500)).
  let totalApplied = 0, totalSkipped = 0;
  const allErrors: string[] = [];
  for (const batch of batchOps(ops)) {
    const result = await localRequest<PushResult>('/sync/push', {
      method: 'POST',
      body: JSON.stringify({ ops: batch }),
    });
    totalApplied += result.applied ?? 0;
    totalSkipped += result.skipped ?? 0;
    if (result.errors?.length) allErrors.push(...result.errors);
  }
  if (allErrors.length) console.warn('[Sync] Local push errors:', allErrors);
  console.log(`[Sync] Local push: applied=${totalApplied} skipped=${totalSkipped} errors=${allErrors.length}`);

  return { count: ops.length, serverTime, serverSeq };
}

async function pushToVps(): Promise<{ count: number; localSeq: number | null }> {
  const seqStr = localStorage.getItem(LAST_PUSHED_SEQ_KEY) ?? '0';
  const data = await localRequest<Record<string, unknown>>(`/sync/pull?since_seq=${seqStr}`);
  const ops = normalizeOps(data);
  const localSeq = typeof data.serverSeq === 'number' ? data.serverSeq : null;
  if (!ops.length) return { count: 0, localSeq };

  // Log orgIds present in push ops — confirms what org rows VPS must have.
  const opRecords = ops as Array<{ table?: string; payload?: Record<string, unknown> }>;
  const byTable = opRecords.reduce<Record<string, number>>((acc, op) => {
    const t = op.table ?? 'unknown'; acc[t] = (acc[t] ?? 0) + 1; return acc;
  }, {});
  const orgIds = [...new Set(opRecords.map((op) => op.payload?.org_id ?? op.payload?.orgId ?? '?'))];
  console.log(`[Sync] VPS push: ${ops.length} ops since_seq=${seqStr}`, byTable, 'orgIds in payload:', orgIds);

  let totalApplied = 0, totalSkipped = 0;
  const allErrors: string[] = [];
  for (const batch of batchOps(ops)) {
    const result = await vpsRequest<PushResult>('/sync/push', {
      method: 'POST',
      body: JSON.stringify({ ops: batch }),
    });
    totalApplied += result.applied ?? 0;
    totalSkipped += result.skipped ?? 0;
    if (result.errors?.length) {
      allErrors.push(...result.errors);
      // Log first failing op payload for FK diagnosis
      const firstErr = result.errors[0];
      const failId = firstErr?.split(':')[1]?.trim().split(' ')[0];
      const failOp = (batch as Array<{ recordId?: string; payload?: unknown }>).find((op) => op.recordId === failId);
      if (failOp) console.error('[Sync] failing op payload:', JSON.stringify(failOp.payload));
    }
  }
  if (allErrors.length) console.warn('[Sync] VPS push errors:', allErrors);
  console.log(`[Sync] VPS push done: applied=${totalApplied} skipped=${totalSkipped} errors=${allErrors.length}`);

  // If any ops failed, don't advance the cursor — those ops must be retried next sync.
  // LWW on VPS makes re-pushing already-applied ops safe (they're just skipped).
  return { count: ops.length, localSeq: allErrors.length === 0 ? localSeq : null };
}

async function downloadMissingImageFiles(): Promise<void> {
  type ImageRow = { cloudUrl: string | null; url: string; filename: string };

  let images: ImageRow[];
  try {
    images = await localRequest<ImageRow[]>('/images');
  } catch {
    return;
  }
  if (!images.length) return;

  let cached = 0;
  for (const img of images) {
    // Prefer R2 cloudUrl; fall back to VPS uploads endpoint.
    // ensureFile saves to userData/uploads/images/ — served by Express at
    // /uploads/images/<filename>, making bgImage URLs work in the receiver preview.
    const sourceUrl = img.cloudUrl ?? `${_vpsUrl}/uploads/images/${img.filename}`;
    const ok = await window.electronAPI!.images.ensureFile(img.filename, sourceUrl);
    if (ok) cached++;
  }
  if (cached) console.log(`[Sync] Image files cached: ${cached}/${images.length}`);
}

// Pushes image binaries to VPS for images that have no cloudUrl (uploaded in
// Electron where R2 creds are absent). VPS handles R2 upload and stores cloudUrl.
// Next pull brings cloudUrl back to Electron.
async function uploadImageBinariesToVps(): Promise<void> {
  if (!window.electronAPI) return;

  type ImageRow = { id: string; filename: string; cloudUrl?: string | null };

  let images: ImageRow[];
  try {
    images = await localRequest<ImageRow[]>('/images');
  } catch {
    return;
  }

  const missing = images.filter((img) => !img.cloudUrl);
  if (!missing.length) return;

  let uploaded = 0;
  for (const img of missing) {
    try {
      const res = await fetch(`/uploads/images/${img.filename}`);
      if (!res.ok) continue;
      // Reject SPA HTML fallback — server returns index.html (text/html) when file is
      // missing and cloudUrl is null. Uploading it would corrupt R2 with HTML content.
      const ct = res.headers.get('content-type') ?? '';
      if (!ct.startsWith('image/')) continue;
      const blob = await res.blob();
      const fd = new FormData();
      fd.append('file', blob, img.filename);
      const token = _getToken ? await _getToken() : _token;
      const r = await fetch(`${_vpsUrl}/api/images/${img.id}/binary`, {
        method: 'PUT',
        headers: vpsAuthHeaders(token),
        body: fd,
      });
      if (r.ok) uploaded++;
    } catch {
      // non-fatal — retry next sync
    }
  }

  if (uploaded) console.log(`[Sync] Image binaries uploaded to VPS: ${uploaded}/${missing.length}`);
}

// Pushes font binaries to VPS for files that have no publicUrl (i.e. were
// uploaded in Electron where R2 creds are absent). VPS handles R2 upload and
// stores publicUrl. Next pull brings publicUrl back to Electron.
async function uploadFontBinariesToVps(): Promise<void> {
  if (!window.electronAPI) return;

  type FontFile = { id: string; filename: string; publicUrl?: string | null };
  type Font = { files: FontFile[] };

  let fonts: Font[];
  try {
    fonts = await localRequest<Font[]>('/fonts');
  } catch {
    return;
  }

  const missing = fonts.flatMap((f) => f.files).filter((ff) => !ff.publicUrl);
  if (!missing.length) return;

  let uploaded = 0;
  for (const ff of missing) {
    const b64 = await window.electronAPI.fonts.readFile(ff.filename);
    if (!b64) continue;

    try {
      const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes]);
      const fd = new FormData();
      fd.append('file', blob, ff.filename);
      const token = _getToken ? await _getToken() : _token;
      const res = await fetch(`${_vpsUrl}/api/fonts/files/${ff.id}/binary`, {
        method: 'PUT',
        headers: vpsAuthHeaders(token),
        body: fd,
      });
      if (res.ok) uploaded++;
    } catch {
      // non-fatal — will retry next sync
    }
  }

  if (uploaded) console.log(`[Sync] Font binaries uploaded to VPS: ${uploaded}/${missing.length}`);
}

async function downloadMissingFontFiles(): Promise<void> {
  type FontFile = { filename: string; publicUrl?: string | null };
  type Font = { files: FontFile[] };

  let fonts: Font[];
  try {
    fonts = await localRequest<Font[]>('/fonts');
  } catch {
    return; // local server not ready or no fonts
  }

  const allFiles = fonts.flatMap((f) => f.files);
  if (!allFiles.length) return;

  let downloaded = 0;
  for (const ff of allFiles) {
    // Prefer R2 public URL (no auth); fallback to VPS static route
    const sourceUrl = ff.publicUrl ?? `${_vpsUrl}/uploads/fonts/${ff.filename}`;
    const ok = await window.electronAPI!.fonts.ensureFile(ff.filename, sourceUrl);
    if (ok) downloaded++;
  }

  if (downloaded) console.log(`[Sync] Font files ensured: ${downloaded}/${allFiles.length}`);
}

async function connectVpsSse(): Promise<void> {
  if (!_token && !_getToken) return;
  if (_sseAbort) _sseAbort.abort();
  _sseAbort = new AbortController();

  const reconnect = async () => {
    while (_token) {
      try {
        const token = _getToken ? await _getToken() : _token;
        const res = await fetch(`${_vpsUrl}/api/sync/events`, {
          headers: vpsAuthHeaders(token),
          signal: _sseAbort!.signal,
        });
        if (!res.ok || !res.body) throw new Error(`SSE ${res.status}`);

        // Catch up any changes missed during dropout.
        runSync().catch(() => {});

        const reader = res.body.getReader();
        const dec = new TextDecoder();
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          if (dec.decode(value, { stream: true }).includes('data: sync')) {
            console.log('[Sync] SSE: remote change detected, syncing...');
            runSync().catch(() => {});
          }
        }
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        console.warn('[Sync] SSE disconnected, retrying in 10s:', (err as Error).message);
      }
      if (!_token) return;
      await new Promise((r) => setTimeout(r, 10_000));
    }
  };

  reconnect().catch(() => {});
}

async function runSyncInner(): Promise<void> {
  try {
    const { count: pulled, serverSeq } = await pullFromVps();
    const { count: pushed, localSeq } = await pushToVps();
    // Advance VPS pull cursor (filters out ops we already have locally).
    if (serverSeq !== null) {
      localStorage.setItem(LAST_SEQ_KEY, String(serverSeq));
    }
    // Advance local push cursor — uses local seq counter, immune to clock skew.
    // Filters out rows the just-applied pull wrote (their sync_seq advanced too,
    // but they're already on VPS so LWW would skip them anyway).
    if (localSeq !== null) {
      localStorage.setItem(LAST_PUSHED_SEQ_KEY, String(localSeq));
    }
    // Push font/image binaries that lack cloud URL to VPS (VPS handles R2 upload).
    // Must run after pushToVps so the metadata row already exists on VPS.
    await uploadFontBinariesToVps();
    await uploadImageBinariesToVps();
    // Download files BEFORE dispatching proclaim:sync.
    // Font binary files must exist on disk before useFontLoader creates @font-face
    // rules — otherwise the browser fetches /uploads/fonts/<file> and gets 404,
    // caching the failure even after ensureFile later writes the file.
    await downloadMissingFontFiles();
    await downloadMissingImageFiles();
    if (pulled || pushed) {
      console.log(`[Sync] Done: pulled=${pulled} pushed=${pushed} serverSeq=${serverSeq} localSeq=${localSeq}`);
      window.dispatchEvent(new CustomEvent('proclaim:sync'));
    } else {
      console.log(`[Sync] Done: no changes (serverSeq=${serverSeq} localSeq=${localSeq})`);
    }
  } catch (err) {
    console.warn('[Sync] Sync failed (offline?):', err instanceof Error ? err.message : err);
  }
}

async function runSync(): Promise<void> {
  if (_running) {
    _pending = true;
    return;
  }
  _running = true;
  try {
    await runSyncInner();
  } finally {
    _running = false;
    if (_pending) {
      _pending = false;
      runSync().catch(() => {});
    }
  }
}

// Shared sync bootstrap: restart local server for the org, seed Bible, run the
// initial sync, then start the periodic timer + SSE. Token/scheme must be set by
// the caller before this runs.
async function startSyncForOrg(orgId: string, orgName?: string, orgSlug?: string): Promise<void> {
  _vpsUrl = await window.electronAPI!.sync.getVpsUrl();

  console.log('[Sync] Setting org:', orgId);
  const setOrgResult = await window.electronAPI!.sync.setOrg(orgId, orgName, orgSlug);
  if (!setOrgResult.ok) {
    console.error('[Sync] setOrg failed:', setOrgResult.error);
    _initializedForOrg = null;
    return;
  }

  // Delay only when server actually restarted (new org or first login).
  // Returning users skip this — server was already running with correct org.
  if (setOrgResult.restarted) {
    await new Promise((r) => setTimeout(r, 800));
  }

  // Reset push cursor on new org session so orphaned ops (from a previous push
  // that errored mid-batch) are recovered. VPS LWW skips already-applied ops.
  localStorage.removeItem(LAST_PUSHED_SEQ_KEY);

  await downloadBibleIfNeeded();
  await runSync();

  if (_syncTimer) clearInterval(_syncTimer);
  _syncTimer = setInterval(runSync, SYNC_INTERVAL_MS);

  connectVpsSse().catch(() => {});
}

/**
 * dev:electron path — Clerk JWT auth (pk_test works in the renderer on localhost).
 * Called by AuthBridge after Clerk login. VPS requests use Authorization: Bearer.
 */
export async function initElectronSync(
  getToken: (forceRefresh?: boolean) => Promise<string | null>,
  orgId: string,
  orgName?: string,
  orgSlug?: string,
): Promise<void> {
  if (!window.electronAPI) return;
  _authScheme = 'bearer';

  // Guard: bridge can remount (StrictMode, route change) without a real org change.
  if (_initializedForOrg === orgId) {
    _getToken = getToken;
    const fresh = await getToken(true);
    if (fresh) _token = fresh;
    return;
  }

  _initializedForOrg = orgId;
  _getToken = getToken;
  // Force-refresh to ensure org claims are present: the cached JWT may have been
  // issued before the user activated an org (org?.id truthy but token stale).
  const token = await getToken(true);
  if (!token) { _initializedForOrg = null; return; }
  _token = token;

  await startSyncForOrg(orgId, orgName, orgSlug);
}

/**
 * Prod Electron path — backend desktop session token (X-Desktop-Token). Called by
 * DesktopBridge after desktop sign-in / org switch. The token is long-lived
 * (60-day sliding), so there is no force-refresh — _getToken just returns it.
 */
export async function initElectronDesktopSync(
  desktopToken: string,
  orgId: string,
  orgName?: string,
  orgSlug?: string,
): Promise<void> {
  if (!window.electronAPI || !desktopToken) return;
  _authScheme = 'desktop';
  const getToken = async () => desktopToken;

  if (_initializedForOrg === orgId) {
    _getToken = getToken;
    _token = desktopToken;
    return;
  }

  _initializedForOrg = orgId;
  _getToken = getToken;
  _token = desktopToken;

  await startSyncForOrg(orgId, orgName, orgSlug);
}

/**
 * Debounced sync trigger — call after any local write.
 * Coalesces rapid edits; pushes to VPS ~2s after last write.
 */
export function scheduleSync(): void {
  if (!window.electronAPI || !_token) return;
  if (_scheduleTimer) clearTimeout(_scheduleTimer);
  _scheduleTimer = setTimeout(() => {
    _scheduleTimer = null;
    runSync().catch(() => {});
  }, 2_000);
}

/** Update token when Clerk refreshes it. */
export function updateSyncToken(token: string | null): void {
  _token = token;
}

/** Update getToken fn when Clerk re-renders (keeps _getToken from going stale). */
export function updateSyncGetToken(getToken: ((forceRefresh?: boolean) => Promise<string | null>) | null): void {
  _getToken = getToken;
}

/** Stop periodic sync (called on logout). */
export function stopElectronSync(): void {
  if (_syncTimer) {
    clearInterval(_syncTimer);
    _syncTimer = null;
  }
  if (_scheduleTimer) {
    clearTimeout(_scheduleTimer);
    _scheduleTimer = null;
  }
  if (_sseAbort) {
    _sseAbort.abort();
    _sseAbort = null;
  }
  _token = null;
  _getToken = null;
  _authScheme = 'bearer';
  _initializedForOrg = null;
  _running = false;
  _pending = false;
  localStorage.removeItem(LAST_SEQ_KEY);
  localStorage.removeItem(LAST_PUSHED_SEQ_KEY);
}

/**
 * Web-browser SSE sync — connects to /api/sync/events and dispatches
 * 'proclaim:sync' so stores refetch. No local SQLite involved.
 * Call after Clerk login when NOT in Electron mode.
 *
 * Must await token before starting SSE — connectWebSse's while(_token) guard
 * exits immediately if _token is still null when called.
 */
export async function initWebSync(getToken: () => Promise<string | null>): Promise<void> {
  if (window.electronAPI) return;
  _getToken = getToken;
  _token = await getToken();
  if (_webSseAbort) _webSseAbort.abort();
  _webSseAbort = new AbortController();
  connectWebSse(_webSseAbort).catch(() => {});
  // Periodic fallback: if SSE is broken, stores still refresh every 5 min.
  if (_webSyncTimer) clearInterval(_webSyncTimer);
  _webSyncTimer = setInterval(
    () => window.dispatchEvent(new CustomEvent('proclaim:sync')),
    SYNC_INTERVAL_MS,
  );
}

/** Update token for web SSE reconnect (Clerk token refresh). */
export function updateWebSyncToken(token: string | null): void {
  if (window.electronAPI) return;
  _token = token;
}

/** Stop web SSE (called on logout). */
export function stopWebSync(): void {
  if (_webSseAbort) {
    _webSseAbort.abort();
    _webSseAbort = null;
  }
  if (_webSyncTimer) {
    clearInterval(_webSyncTimer);
    _webSyncTimer = null;
  }
  _token = null;
  _getToken = null;
}

async function connectWebSse(abort: AbortController): Promise<void> {
  while (_token) {
    try {
      const token = _getToken ? await _getToken() : _token;
      const res = await fetch('/api/sync/events', {
        headers: { Authorization: `Bearer ${token}` },
        signal: abort.signal,
      });
      if (!res.ok || !res.body) throw new Error(`SSE ${res.status}`);

      // Fire immediately on (re)connect to catch any changes missed during dropout.
      window.dispatchEvent(new CustomEvent('proclaim:sync'));

      const reader = res.body.getReader();
      const dec = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (dec.decode(value, { stream: true }).includes('data: sync')) {
          console.log('[Sync] SSE: remote change detected');
          window.dispatchEvent(new CustomEvent('proclaim:sync'));
        }
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      console.warn('[Sync] Web SSE disconnected, retrying in 10s:', (err as Error).message);
    }
    if (!_token) return;
    await new Promise((r) => setTimeout(r, 10_000));
  }
}
